import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type {
  ApprovalInboxPage,
  ApprovalInboxRow,
  ApprovalState,
} from '../models/platformAction.types'
import type { PlatformActionError } from '../models/platformActionError'

import type { PlatformActionLoadStatus } from './platformActionStore'

/** The server's default page size; it clamps at 200. */
export const INBOX_PAGE_SIZE = 50

/**
 * The "waiting on a decision" tab's state. Pure container — `LoadApprovalsFlow` writes it.
 *
 * It does not own approval state: a decision can land from chat or Slack while the page is open,
 * so rows are only ever replaced by a fresh read, never patched from a decide response.
 */
export const useApprovalInboxStore = defineStore('platformActionApprovals', () => {
  const rows = ref<ApprovalInboxRow[]>([])
  const page = ref(1)
  const pageSize = ref(INBOX_PAGE_SIZE)
  const total = ref(0)
  const scanTruncated = ref(false)
  /** `''` = every state. */
  const stateFilter = ref<ApprovalState | ''>('')
  const awaitingDecision = ref(0)
  const status = ref<PlatformActionLoadStatus>('idle')
  const error = ref<PlatformActionError | null>(null)
  const loadedKey = ref<string | null>(null)

  /** Loaded, a filter is on, nothing matched — a different message from the first-use empty. */
  const isFilteredEmpty = computed(
    () => status.value === 'ready' && rows.value.length === 0 && stateFilter.value !== '',
  )

  function isCurrent(key: string): boolean {
    return loadedKey.value === key
  }

  function setLoading(key: string): void {
    loadedKey.value = key
    status.value = 'loading'
    error.value = null
  }

  function setPage(key: string, next: ApprovalInboxPage): boolean {
    // A silent refresh never called `setLoading`, so it adopts the key it was built from — but
    // only while nothing newer has claimed the store.
    if (loadedKey.value !== null && !isCurrent(key)) return false
    loadedKey.value = key
    rows.value = next.rows
    page.value = next.page
    if (next.pageSize > 0) pageSize.value = next.pageSize
    total.value = next.total
    scanTruncated.value = next.scanTruncated
    status.value = 'ready'
    error.value = null
    return true
  }

  function setError(key: string, next: PlatformActionError): boolean {
    if (loadedKey.value !== null && !isCurrent(key)) return false
    // A refresh that fails leaves what is already on screen — the last read is still the truest
    // thing we have. Only a first load turns into an error state.
    if (status.value === 'ready' && rows.value.length > 0) return false
    rows.value = []
    total.value = 0
    error.value = next
    status.value = 'error'
    return true
  }

  function setAwaitingDecision(count: number): void {
    awaitingDecision.value = count
  }

  function setStateFilter(next: ApprovalState | ''): void {
    stateFilter.value = next
    page.value = 1
  }

  function setPageNumber(next: number): void {
    page.value = next
  }

  function setPageSize(next: number): void {
    pageSize.value = next
    page.value = 1
  }

  function reset(): void {
    rows.value = []
    page.value = 1
    pageSize.value = INBOX_PAGE_SIZE
    total.value = 0
    scanTruncated.value = false
    stateFilter.value = ''
    awaitingDecision.value = 0
    status.value = 'idle'
    error.value = null
    loadedKey.value = null
  }

  return {
    rows,
    page,
    pageSize,
    total,
    scanTruncated,
    stateFilter,
    awaitingDecision,
    status,
    error,
    loadedKey,
    isFilteredEmpty,
    isCurrent,
    setLoading,
    setPage,
    setError,
    setAwaitingDecision,
    setStateFilter,
    setPageNumber,
    setPageSize,
    reset,
  }
})

export type ApprovalInboxStoreInstance = ReturnType<typeof useApprovalInboxStore>
